import React from "react";

import { ReactComponent as ContradictionsIcon } from "../../../../assets/images/contradiction-icon.svg";
import { ReactComponent as EurekaIcon } from "../../../../assets/images/eureka-icon-blue.svg";
import { ReactComponent as DetailsMatrixIcon } from "../../../../assets/images/nav-attribute-dependency.svg";
import { ReactComponent as MultiplicationIcon } from "../../../../assets/images/nav-multiplication.svg";
import { ReactComponent as ReplacementIcon } from "../../../../assets/images/nav-replacement.svg";
import { ReactComponent as SubtractionIcon } from "../../../../assets/images/nav-subtraction.svg";
import { getInitials } from "../../../../helpers/getInitials";
import TranslateTemplate from "../../../common/TranslateTemplate";
import CustomCheckbox from "../../../inputs/CustomCheckbox";

const ideasIcons = {
  dependency: <DetailsMatrixIcon width={26} />,
  subtraction: <SubtractionIcon width={26} />,
  replacement: <ReplacementIcon width={26} />,
  multiplication: <MultiplicationIcon width={26} />,
  eureka: <EurekaIcon width={26} />,
  contradictionSolving: <ContradictionsIcon width={26} height={26} />,
};

const colors = {
  1: "#ffcd67",
  2: "#f9ae9f",
  3: {
    1: "#daf1e1",
    2: "#b5e3c3",
    3: "#91d5a5",
    4: "#6cc687",
    5: "#5ac57a",
  },
};

const colorClass = {
  1: "yellow",
  2: "red",
  3: "green",
  4: "white",
};

const RowIdea = ({ row, ideasPicked, handlePickedIdea }) => {
  const ideaAbbreviation =
    row.type === "dependency"
      ? "AD"
      : row.type === "subtraction"
        ? "S"
        : row.type === "replacement"
          ? "R"
          : row.type === "multiplication"
            ? "M"
            : row.type === "contradictionSolving"
              ? "C"
              : "EU";
  const iconWithAbbreviation = (
    <div className="ideas-row-method flex">
      {ideasIcons[row.type]}
      <span className="ideas-row-text abbreviation">{ideaAbbreviation}</span>
    </div>
  );

  const getRating = () => {
    const rating = row.rating || 0;
    const feasibleRating = row.feasibleRating || 0;

    if (!row?.feasibleRating || row.feasibleRating < 0) {
      return Math.min(rating, 5);
    }

    const sum = rating + feasibleRating;
    return Math.round(sum / 2);
  };

  const getStatusColor = () => {
    if (row.status < 3) {
      return colors[row.status];
    }
    return colors[row.status][row.rating];
  };

  const getColorDropdownElem = () => {
    if (row.status === 3) {
      return colorClass[3];
    }

    if (row.feasibleRating === 0 || row.rating === 0) {
      return colorClass[2];
    }

    if (row.status === 1) {
      return colorClass[1];
    }

    return colorClass[4];
  };

  const rightAttribute =
    !row.remoteComponent && !row.idMultiplicationComponent && row.description === undefined
      ? row.rowStatus?.locked
        ? row.firstAttribute
        : `${row.firstAttribute} – ${row.firstComponent}`
      : "";
  const leftAttribute =
    !row.remoteComponent && !row.idMultiplicationComponent && row.description === undefined
      ? row.colStatus?.locked
        ? row.secondAttribute
        : `${row.secondAttribute} – ${row.secondComponent}`
      : "";
  const allBenefitsCount = row?.benefits?.filter((benefit) => benefit.target.length > 0).length;
  const isShowBenefits = allBenefitsCount > 0 ? allBenefitsCount : null;
  const componentName = row.idReplacingComponent
    ? row.replacingComponent
    : row.idRemoteComponent
      ? row.remoteComponent
      : row.idMultiplicationComponent
        ? row.remoteComponent
        : "";

  const checked =
    ideasPicked.contradictionsSolvingsIds.includes(row.id) ||
    ideasPicked.detailsMatrixIds.includes(row.id) ||
    ideasPicked.eurekasIds.includes(row.id) ||
    ideasPicked.replacmentsIds.includes(row.id) ||
    ideasPicked.multiplicationsIds.includes(row.id) ||
    ideasPicked.subtractionsIds.includes(row.id);

  return (
    <div
      className={"ideas-row flex align-center pointer " + getColorDropdownElem()}
      onClick={() => handlePickedIdea(row)}
    >
      <div className="ideas-row-color-rating" style={{ backgroundColor: getStatusColor() }}></div>

      <div className="ideas-row-checkbox flex-center">
        <CustomCheckbox checked={checked} handleChange={() => handlePickedIdea(row)} customClass="ideas-checkbox" />
      </div>

      <div className="ideas-row-rating justify-content-start">
        {row.status === 3 && row.rating > 0 && (
          <div className="report-idea-score flex flex-center" style={{ backgroundColor: getStatusColor() }}>
            <span>{getRating()}</span>
          </div>
        )}
      </div>

      <div className="ideas-row-name flex">
        <span className="ideas-row-text">{row.name}</span>
      </div>

      {iconWithAbbreviation}

      <div className="border-report border-left-report" />

      <div className="ideas-row-component flex">
        <span className="ideas-row-text">{componentName}</span>
      </div>

      <div className="border-report border-right-report" />

      <div className="ideas-row-first-attribute flex">
        <span className="ideas-row-text">{leftAttribute}</span>
      </div>

      <div className="ideas-row-second-attribute flex">
        <span className="ideas-row-text">{rightAttribute}</span>
      </div>

      <div className="border-report border-right-report" />

      <div className="ideas-row-benefit-count flex">
        <span className="ideas-row-text benefits-count">{isShowBenefits}</span>
      </div>

      <div className="ideas-row-project flex">
        <span className="ideas-row-text">{row.productName}</span>
      </div>

      <div className="ideas-row-created-by flex">
        <div className="ideas-row-initials flex-center">
          <TranslateTemplate as="span">{getInitials(row.createdBy || "")}</TranslateTemplate>
        </div>
      </div>
    </div>
  );
};

export default RowIdea;
